export const REDACTED = '[redacted]';

/**
 * Names of URL parameters that carry an email, a credential or wallet
 * material. Compared in lower case, after percent-decoding. Each name has a
 * call site in r-client:
 * - `data`: the new-device pairing link, `?data=<JSON with email, wallet id
 *   and the encrypted backup share>` (`create-backup-passkey-qr.tsx`,
 *   `link-new-device.tsx`, read in `use-link-new-device-mfa.tsx`).
 * - `email`: sign-up and verify-email pages (`auth/register/index.page.tsx`,
 *   `auth/verify-email/index.page.tsx`, `get-auth-page-props.ts`).
 * - `user[email]`: the login links of the device and transfer flows
 *   (`generate-device-backup-url.ts`, `use-complete-transfer.ts`).
 * - `token`: the partner token of `/rw/<flow>/<partner>` and of the INX trade
 *   page (`validate-partner-request.ts`, `trade-with-inx/index.page.tsx`).
 */
const SENSITIVE_PARAMS: readonly string[] = [
  'data',
  'email',
  'user[email]',
  'token',
];

/** How many times a value may be percent-decoded in search of a nested URL. */
const MAX_NESTING = 3;

/**
 * A raw (unencoded) JSON `data` value runs to the end of the string: its
 * `&`, `?` and `#` are part of the JSON. Redact it before any split.
 */
const redactRawJsonData = (text: string): string =>
  text.replace(/((?:^|[?&])data=)\{[\s\S]*$/i, `$1${REDACTED}`);

/**
 * Decodes every valid `%XX` run and leaves a malformed one (a lone `%`,
 * invalid UTF-8) as it is. `decodeURIComponent` throws on such a string, and
 * a throw would leave the whole value unredacted. Never throws.
 */
const decodeLenient = (text: string): string =>
  text.replace(/(?:%[0-9a-f]{2})+/gi, (run) => {
    try {
      return decodeURIComponent(run);
    } catch {
      return run;
    }
  });

/**
 * Redacts a sensitive parameter that hides inside the value of another one,
 * such as `after_sign_in=%2Fbackup%3Ftoken%3Dfake`. Decodes the value, looks
 * into it, and encodes it again only when something changed, so an unchanged
 * value stays byte for byte.
 */
const redactNestedValue = (value: string, depth: number): string => {
  if (depth >= MAX_NESTING) {
    return value;
  }
  try {
    const decoded = decodeLenient(value);
    const inner = /[?&]/.test(decoded)
      ? redactUrl(decoded, depth + 1)
      : redactNestedValue(decoded, depth + 1);
    return inner === decoded ? value : encodeURIComponent(inner);
  } catch {
    return value;
  }
};

/**
 * Redacts the value of every sensitive parameter in a query string (with or
 * without a leading `?`). Splits on `&` and also on `?`, because the login
 * links nest a second query: `after_sign_in=/backup?data=...`. A pair without
 * `=` and every other pair stay byte for byte, except that a sensitive
 * parameter nested in an encoded value is redacted too.
 */
export const redactQueryString = (query: string, depth = 0): string =>
  redactRawJsonData(query)
    .split(/([&?])/)
    .map((part, index) => {
      const eq = part.indexOf('=');
      // Odd indexes are the delimiters that `split` keeps.
      if (index % 2 || eq < 0) {
        return part;
      }
      const name = decodeLenient(part.slice(0, eq)).toLowerCase();
      const head = part.slice(0, eq + 1);
      return SENSITIVE_PARAMS.includes(name)
        ? `${head}${REDACTED}`
        : `${head}${redactNestedValue(part.slice(eq + 1), depth)}`;
    })
    .join('');

/**
 * Redacts the sensitive parameters of a URL. Works on the string, so a
 * relative URL, `mailto:` and `data:` need no special case. The part before
 * the first `?` stays as it is. The hash gets the same redaction, as a cheap
 * guard for a hash that holds parameters (`#a=b&data=c`). Never throws.
 */
export const redactUrl = (rawUrl: string, depth = 0): string => {
  const url = redactRawJsonData(rawUrl);
  const hashAt = url.indexOf('#');
  const beforeHash = hashAt < 0 ? url : url.slice(0, hashAt);
  const hash =
    hashAt < 0 ? '' : `#${redactQueryString(url.slice(hashAt + 1), depth)}`;
  const queryAt = beforeHash.indexOf('?');
  if (queryAt < 0) {
    return `${beforeHash}${hash}`;
  }
  const path = beforeHash.slice(0, queryAt + 1);
  const query = redactQueryString(beforeHash.slice(queryAt + 1), depth);
  return `${path}${query}${hash}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** One guard per field: a field that cannot be written is skipped. */
const redactField = (
  target: unknown,
  key: string,
  redact: (value: string) => string
): void => {
  if (!isRecord(target)) {
    return;
  }
  try {
    const value = target[key];
    if (typeof value === 'string') {
      target[key] = redact(value);
    }
  } catch {
    // Frozen or odd object: go on with the next field.
  }
};

/**
 * Redacts sensitive parameters in every URL that the Rollbar browser SDK puts
 * into a payload: `request.url`, `request.query_string`, and the telemetry
 * events (`from` and `to` of a navigation, `url` of a network event).
 * Mutates the payload. Never throws: a reporter that throws loses the error.
 */
export const redactPayloadUrls = (payload: unknown): void => {
  try {
    if (!isRecord(payload)) {
      return;
    }
    const { request, body } = payload;
    redactField(request, 'url', redactUrl);
    redactField(request, 'query_string', redactQueryString);

    const telemetry = isRecord(body) ? body['telemetry'] : undefined;
    for (const event of Array.isArray(telemetry) ? telemetry : []) {
      const eventBody = isRecord(event) ? event['body'] : undefined;
      for (const key of ['from', 'to', 'url']) {
        redactField(eventBody, key, redactUrl);
      }
    }
  } catch {
    // Keep the payload as it is: losing the report is worse.
  }
};
